<script setup lang="ts">
import { ref } from 'vue';
import { RadioGroup, RadioGroupDescription, RadioGroupLabel, RadioGroupOption } from '@headlessui/vue';
import type { IconName } from '@/helpers/icons';
import type { LendingProduct } from '@/types';

const productOptions: Array<{
  id: LendingProduct;
  title: string;
  term: string;
  icon: IconName;
  description: string[];
}> = [
  {
    id: 'bridging',
    title: 'Bridging Loan',
    term: '6 - 24 month term',
    icon: 'buyNow',
    description: [
      'Buy now, sell later - buy your next home before you have sold your existing home.',
      "Single security equity release - access funds from the home you're selling, before you sell.",
    ],
  },
  {
    id: 'erhl',
    title: 'Equity Release Home Loan',
    term: '30 year term',
    icon: 'timeLoan',
    description: [
      'Unlock the equity in your home with a 30 year interest only loan, to support your lifestyle, financial goals or retirement - delaying the need to sell.',
    ],
  },
];

const props = defineProps<{ modelValue?: LendingProduct | null }>();

const selectedProduct = ref<LendingProduct | undefined>(props.modelValue ?? undefined);

const emit = defineEmits<{ select: [product: LendingProduct] }>();

/**
 * Emits the chosen product to the parent once a selection has been made.
 * @returns {void}
 */
const handleNext = (): void => {
  if (!selectedProduct.value) return;
  emit('select', selectedProduct.value);
};
</script>

<template>
  <div class="flex flex-1 flex-col">
    <div class="mt-8">
      <RadioGroup v-model="selectedProduct">
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <RadioGroupOption
            v-for="product in productOptions"
            v-slot="{ checked, active }"
            :key="product.id"
            :value="product.id"
            class="hover:border-bridgit-vibrantGreen"
            as="template"
          >
            <div>
              <div
                :class="[
                  checked ? 'border-transparent' : 'border-gray-300',
                  active
                    ? 'border-bridgit-vibrantGreen ring-2 ring-bridgit-vibrantGreen'
                    : 'hover:border-bridgit-lightGreen hover:ring-2 hover:ring-bridgit-lightGreen',
                  'relative flex h-full cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none ',
                ]"
              >
                <span class="flex gap-4">
                  <div class="basis-12 pt-2 text-bridgit-vibrantGreen">
                    <BxIcon :name="product.icon" />
                  </div>
                  <span class="flex flex-grow flex-col">
                    <span class="mb-2 flex flex-wrap items-center gap-2">
                      <RadioGroupLabel as="span" class="block text-justify text-sm font-medium text-gray-900">
                        {{ product.title }}
                      </RadioGroupLabel>
                      <span
                        class="whitespace-nowrap rounded-md bg-bridgit-lightGreen px-2 py-0.5 text-xs font-medium text-bridgit-green"
                      >
                        {{ product.term }}
                      </span>
                    </span>
                    <RadioGroupDescription as="span" class="mt-1 flex flex-col gap-2 text-left text-sm text-gray-500">
                      <span v-for="paragraph in product.description" :key="paragraph">{{ paragraph }}</span>
                    </RadioGroupDescription>
                  </span>
                </span>
                <span
                  :class="[
                    active ? 'border' : 'border-2',
                    checked ? 'border-bridgit-vibrantGreen' : 'border-transparent',
                    'pointer-events-none absolute -inset-px rounded-lg',
                  ]"
                  aria-hidden="true"
                />
              </div>
            </div>
          </RadioGroupOption>
        </div>
      </RadioGroup>

      <!--
        Both checklists are always rendered in the same grid cell, with the inactive one hidden, so the
        panel keeps the height of the longest list and the card doesn't resize when switching products.
      -->
      <div
        class="mt-8 rounded-lg bg-bridgit-paleBlue p-6 leading-7"
        :class="!selectedProduct && 'invisible'"
        :aria-hidden="!selectedProduct"
      >
        <p class="text-left font-bold">Below is what you'll need to complete the application:</p>
        <div class="mt-4 grid">
          <ul
            class="col-start-1 row-start-1 list-none pl-2 text-left"
            :class="selectedProduct !== 'bridging' && 'invisible'"
            :aria-hidden="selectedProduct !== 'bridging'"
          >
            <li><span>Details of the properties being purchased and sold</span></li>
            <li><span>Details of all the applicants and borrowers (individual, company, trust)</span></li>
            <li><span>Income and expense details (if there is any residual debt)</span></li>
          </ul>
          <ul
            class="col-start-1 row-start-1 list-none pl-2 text-left"
            :class="selectedProduct !== 'erhl' && 'invisible'"
            :aria-hidden="selectedProduct !== 'erhl'"
          >
            <li><span>Details of the property or properties being used as security</span></li>
            <li><span>Details of all applicants on the application</span></li>
            <li><span>Details of income, expense, asset and liabilities</span></li>
            <li><span>Loan structure (Lump sum, with optional scheduled equity release)</span></li>
          </ul>
        </div>
      </div>
    </div>

    <div class="mt-auto flex justify-end pt-6">
      <BxButton id="product-selection-next" variant="primary" :disabled="!selectedProduct" @click="handleNext">
        Next
      </BxButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
ul {
  li {
    @apply mt-0.5;

    &::before {
      @apply text-bridgit-vibrantGreen;
      @apply mr-[-1.125rem] rounded-full font-black;
      content: '✓';
      padding: 2px 4px;
      font-size: 10px;
      background-color: #eeecec;
    }
  }

  span {
    @apply relative left-6 top-[2px] mr-4 inline-flex;
  }
}
</style>
